import React, { useState } from 'react';
import { ArrowLeft, Save, X, Key, FileText, Lock, Unlock } from 'lucide-react';

const PermissionForm = ({ onBack, permissionData, mode }) => {
  const isEditMode = mode === 'edit';
  
  const [formData, setFormData] = useState({
    name: permissionData?.name || '',
    displayName: permissionData?.displayName || '',
    resourceType: permissionData?.resourceType || '',
    action: permissionData?.action || '',
    description: permissionData?.description || '',
    type: permissionData?.type || 'Business'
  });

  const [errors, setErrors] = useState({});

  // Available resource types
  const resourceTypes = [
    'User',
    'Role',
    'Permission',
    'Project',
    'Budget',
    'Report',
    'Customer',
    'SPK',
    'TowerSite',
    'FOSegment',
    'Monitoring'
  ];

  // Available actions
  const actions = [
    'CREATE',
    'READ',
    'UPDATE',
    'DELETE',
    'GENERATE',
    'EXPORT',
    'APPROVE',
    'REJECT'
  ];

  // Permission types
  const permissionTypes = [
    { value: 'System', label: 'System', icon: Lock, color: 'purple' },
    { value: 'Business', label: 'Business', icon: Unlock, color: 'orange' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate permission name when resource type or action changes
    if (name === 'resourceType' || name === 'action') {
      const resource = name === 'resourceType' ? value : formData.resourceType;
      const action = name === 'action' ? value : formData.action;
      
      if (resource && action) {
        const generatedName = `${resource.toLowerCase()}.${action.toLowerCase()}`;
        setFormData(prev => ({
          ...prev,
          name: generatedName
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Permission name is required';
    } else if (!/^[a-z]+\.[a-z]+$/.test(formData.name)) {
      newErrors.name = 'Permission name must be in format: resource.action (e.g., user.create)';
    }

    // Display name validation
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    } else if (formData.displayName.length < 3) {
      newErrors.displayName = 'Display name must be at least 3 characters';
    }

    // Resource type validation
    if (!formData.resourceType) {
      newErrors.resourceType = 'Resource type is required';
    }

    // Action validation
    if (!formData.action) {
      newErrors.action = 'Action is required';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      console.log('Form submitted:', formData);
      alert(isEditMode ? 'Permission updated successfully!' : 'Permission created successfully!');
      onBack();
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the form?')) {
      setFormData({
        name: permissionData?.name || '',
        displayName: permissionData?.displayName || '',
        resourceType: permissionData?.resourceType || '',
        action: permissionData?.action || '',
        description: permissionData?.description || '',
        type: permissionData?.type || 'Business'
      });
      setErrors({});
    }
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'text-green-600',
      'READ': 'text-blue-600',
      'UPDATE': 'text-yellow-600',
      'DELETE': 'text-red-600',
      'GENERATE': 'text-purple-600',
      'EXPORT': 'text-indigo-600',
      'APPROVE': 'text-teal-600',
      'REJECT': 'text-orange-600'
    };
    return colors[action] || 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Permission' : 'Add New Permission'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update permission information' : 'Create a new permission'}
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Form Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
            <h2 className="text-lg font-bold text-gray-800">Permission Information</h2>
          </div>

          {/* Form Body */}
          <div className="p-6 space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                Basic Information
              </h3>
              <div className="space-y-4">
                {/* Permission Name (Code) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Permission Name (Code) <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isEditMode && permissionData?.type === 'System'}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    } ${isEditMode && permissionData?.type === 'System' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="e.g., project.create"
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    Format: resource.action (lowercase, no spaces). Auto-generated from resource type and action.
                  </p>
                  {isEditMode && permissionData?.type === 'System' && (
                    <p className="text-blue-600 text-xs mt-1">System permission name cannot be changed</p>
                  )}
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Display Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.displayName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Create Project"
                  />
                  {errors.displayName && (
                    <p className="text-red-600 text-sm mt-1">{errors.displayName}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    User-friendly name displayed in the UI
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe what this permission allows..."
                  />
                  {errors.description && (
                    <p className="text-red-600 text-sm mt-1">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Permission Details Section */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Permission Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Resource Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Resource Type <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="resourceType"
                    value={formData.resourceType}
                    onChange={handleChange}
                    disabled={isEditMode && permissionData?.type === 'System'}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.resourceType ? 'border-red-500' : 'border-gray-300'
                    } ${isEditMode && permissionData?.type === 'System' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select resource type</option>
                    {resourceTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.resourceType && (
                    <p className="text-red-600 text-sm mt-1">{errors.resourceType}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    The resource this permission applies to
                  </p>
                </div>

                {/* Action */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Action <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="action"
                    value={formData.action}
                    onChange={handleChange}
                    disabled={isEditMode && permissionData?.type === 'System'}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.action ? 'border-red-500' : 'border-gray-300'
                    } ${isEditMode && permissionData?.type === 'System' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select action</option>
                    {actions.map(action => (
                      <option key={action} value={action} className={getActionColor(action)}>
                        {action}
                      </option>
                    ))}
                  </select>
                  {errors.action && (
                    <p className="text-red-600 text-sm mt-1">{errors.action}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">
                    The action that can be performed
                  </p>
                </div>
              </div>
            </div>

            {/* Permission Type Section */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-md font-bold text-gray-800 mb-4">
                Permission Type <span className="text-red-600">*</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {permissionTypes.map(({ value, label, icon: Icon, color }) => (
                  <label
                    key={value}
                    className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.type === value
                        ? `border-${color}-500 bg-${color}-50`
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    } ${isEditMode && permissionData?.type === 'System' ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={value}
                      checked={formData.type === value}
                      onChange={handleChange}
                      disabled={isEditMode && permissionData?.type === 'System'}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        formData.type === value
                          ? `bg-${color}-100`
                          : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          formData.type === value
                            ? `text-${color}-600`
                            : 'text-gray-400'
                        }`} />
                      </div>
                      <div>
                        <div className={`font-semibold ${
                          formData.type === value
                            ? `text-${color}-900`
                            : 'text-gray-900'
                        }`}>
                          {label}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {value === 'System' 
                            ? 'Core system permissions (protected)' 
                            : 'Business logic permissions (editable)'}
                        </div>
                      </div>
                    </div>
                    {formData.type === value && (
                      <div className={`w-5 h-5 rounded-full bg-${color}-600 flex items-center justify-center`}>
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {isEditMode && permissionData?.type === 'System' && (
                <p className="text-blue-600 text-sm mt-3">
                  System permission type cannot be changed to maintain system integrity
                </p>
              )}
            </div>

            {/* Preview Section */}
            {formData.name && formData.displayName && (
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-md font-bold text-gray-800 mb-4">Preview</h3>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                      <Key className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{formData.displayName}</h4>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          formData.type === 'System' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {formData.type}
                        </span>
                        {formData.action && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            formData.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                            formData.action === 'READ' ? 'bg-blue-100 text-blue-800' :
                            formData.action === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                            formData.action === 'DELETE' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {formData.action}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{formData.description || 'No description yet'}</p>
                      <p className="text-xs font-mono text-gray-500 bg-white px-2 py-1 rounded inline-block">
                        {formData.name}
                      </p>
                      {formData.resourceType && (
                        <p className="text-xs text-gray-500 mt-2">
                          Resource: <span className="font-semibold">{formData.resourceType}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Reset
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
              >
                <Save className="w-5 h-5" />
                {isEditMode ? 'Update Permission' : 'Create Permission'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermissionForm;